/**
 * Spaces Page
 * Shows list of spaces (high-level containers for saved queries)
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  IconButton,
  InputBase,
  InputAdornment,
  TextField as MuiTextField,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
} from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/date';
import { listSpaces, createSpace, updateSpace, deleteSpace, Space } from '../services/spacesService';

export default function SpacesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [spaces, setSpaces] = React.useState<Space[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'name' | 'created_at' | 'query_count'>('created_at');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
  
  // Create space state
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState('');
  const [createDesc, setCreateDesc] = React.useState('');
  const [createErr, setCreateErr] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  
  // Edit space state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editSpace, setEditSpace] = React.useState<Space | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');
  const [editErr, setEditErr] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  
  // Delete space state
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [delSpace, setDelSpace] = React.useState<Space | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Fetch spaces
  const fetchSpaces = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listSpaces();
      setSpaces(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load spaces');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  // Filter and sort spaces
  const filteredAndSortedSpaces = React.useMemo(() => {
    let filtered = spaces;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = spaces.filter(space =>
        space.name.toLowerCase().includes(query) ||
        space.description?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'query_count':
          comparison = (a.query_count || 0) - (b.query_count || 0);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [spaces, searchQuery, sortBy, sortOrder]);

  const handleSpaceClick = (spaceId: string) => {
    navigate(`/spaces/${spaceId}`);
  };
  
  const handleSort = (column: 'name' | 'created_at' | 'query_count') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  
  const handleCreate = async () => {
    setCreateErr(null);
    const name = createName.trim();
    if (!name) { setCreateErr('Name is required'); return; }
    setCreating(true);
    try {
      await createSpace(name, createDesc.trim() || undefined);
      await fetchSpaces();
      setCreateOpen(false);
      setCreateName('');
      setCreateDesc('');
    } catch (e: any) {
      setCreateErr(e?.message || 'Failed to create space');
    } finally {
      setCreating(false);
    }
  };
  
  const handleEdit = async () => {
    setEditErr(null);
    if (!editSpace) return;
    const name = editName.trim();
    if (!name) { setEditErr('Name is required'); return; }
    setSaving(true);
    try {
      await updateSpace(editSpace.space_id, name, editDesc.trim() || undefined);
      await fetchSpaces();
      setEditOpen(false);
      setEditSpace(null);
    } catch (e: any) {
      setEditErr(e?.message || 'Failed to update space');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!delSpace) return;
    setDeleting(true);
    try {
      await deleteSpace(delSpace.space_id);
      await fetchSpaces();
      setDeleteOpen(false);
      setDelSpace(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete space');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Page Header - compact toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { sm: 36 },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
          Organize your saved insights into categories
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <InputBase
            placeholder="Search categories…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={(t) => ({ color: t.tokens.text.tertiary, ml: 0.5, fontSize: 16 })} />
              </InputAdornment>
            }
            sx={(t) => ({
              width: { xs: '100%', sm: 240 },
              px: 1.25,
              py: 0.5,
              border: `1px solid ${t.tokens.border.default}`,
              borderRadius: `${t.ds.radius.sm}px`,
              fontSize: '0.8125rem',
              height: 32,
              bgcolor: t.tokens.surface.raised,
              transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              '&:hover': { borderColor: t.tokens.border.strong },
              '&:focus-within': { borderColor: t.tokens.brand.primary, boxShadow: t.tokens.shadow.focusRing },
            })}
          />
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineRounded />}
            size="small"
            onClick={() => setCreateOpen(true)}
            sx={(t) => ({
              borderRadius: `${t.ds.radius.sm}px`,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              height: 32,
              fontSize: '0.8125rem',
              fontWeight: 600,
              boxShadow: 'none',
              alignSelf: { xs: 'stretch', sm: 'auto' },
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {isMobile ? 'New' : 'New category'}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State — token-driven skeletons */}
      {loading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Paper
              key={i}
              variant="outlined"
              sx={(t) => ({
                p: 2.5,
                minHeight: 140,
                borderRadius: `${t.ds.radius.lg}px`,
                borderColor: t.tokens.border.subtle,
                bgcolor: t.tokens.surface.raised,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              })}
            >
              <Box sx={(t) => ({ width: 32, height: 32, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.sm}px` })} />
              <Box sx={(t) => ({ width: '60%', height: 14, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
              <Box sx={(t) => ({ width: '90%', height: 10, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.xs}px` })} />
            </Paper>
          ))}
        </Box>
      ) : filteredAndSortedSpaces.length === 0 ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
            borderStyle: 'dashed',
            borderColor: t.tokens.border.default,
            borderRadius: `${t.ds.radius.lg}px`,
            bgcolor: t.tokens.surface.sunken,
          })}
        >
          <Box sx={(t) => ({
            width: 44, height: 44, mb: 1.5,
            borderRadius: `${t.ds.radius.md}px`,
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.brand.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          })}>
            <FolderIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography sx={(t) => ({ mb: 0.5, fontSize: '0.9375rem', fontWeight: 600, color: t.tokens.text.primary })}>
            {searchQuery ? 'No categories found' : 'No categories yet'}
          </Typography>
          <Typography sx={(t) => ({ fontSize: '0.8125rem', color: t.tokens.text.secondary, textAlign: 'center', maxWidth: 360 })}>
            {searchQuery
              ? 'Try adjusting your search terms'
              : 'Save insights from the chat to create your first category'}
          </Typography>
        </Paper>
      ) : (
        /* Spaces Cards — clean neutral chrome, brand-accent stripe on hover,
           hover-reveal actions. No "No description" placeholder when empty. */
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, minWidth: 0 }}>
          {filteredAndSortedSpaces.map((space) => (
            <Card
              key={space.space_id}
              variant="outlined"
              sx={(t) => ({
                position: 'relative',
                borderRadius: `${t.ds.radius.lg}px`,
                overflow: 'hidden',
                borderColor: t.tokens.border.subtle,
                bgcolor: t.tokens.surface.raised,
                transition: `border-color ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}, box-shadow ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}, transform ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, bottom: 0,
                  width: 3,
                  bgcolor: t.tokens.brand.primary,
                  opacity: 0,
                  transition: `opacity ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
                },
                '&:hover': {
                  borderColor: t.tokens.border.strong,
                  boxShadow: t.tokens.shadow.md,
                  transform: 'translateY(-1px)',
                  '&::before': { opacity: 1 },
                  '& .space-card-actions': { opacity: 1 },
                },
                '&:focus-within': { borderColor: t.tokens.brand.primary, boxShadow: t.tokens.shadow.focusRing },
              })}
            >
              <CardActionArea
                onClick={() => handleSpaceClick(space.space_id)}
                sx={{ alignItems: 'stretch', height: '100%' }}
                disableRipple
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={(t) => ({
                      width: 32,
                      height: 32,
                      borderRadius: `${t.ds.radius.sm}px`,
                      bgcolor: t.tokens.brand.primarySoft,
                      color: t.tokens.brand.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    })}>
                      <FolderIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box
                      className="space-card-actions"
                      sx={{ display: 'flex', gap: 0.25, opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms ease' }}
                    >
                      <Tooltip title="Edit category">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditSpace(space);
                            setEditName(space.name);
                            setEditDesc(space.description || '');
                            setEditOpen(true);
                          }}
                          sx={(t) => ({
                            width: 28, height: 28,
                            color: t.tokens.text.tertiary,
                            '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                          })}
                        >
                          <EditRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete category">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDelSpace(space);
                            setDeleteOpen(true);
                          }}
                          sx={(t) => ({
                            width: 28, height: 28,
                            color: t.tokens.text.tertiary,
                            '&:hover': { color: t.tokens.semantic.danger, bgcolor: t.tokens.semantic.dangerSoft },
                          })}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography
                    sx={(t) => ({
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      lineHeight: 1.3,
                      letterSpacing: '-0.005em',
                      color: t.tokens.text.primary,
                      mb: space.description ? 0.5 : 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                    })}
                  >
                    {space.name}
                  </Typography>
                  {space.description ? (
                    <Typography
                      sx={(t) => ({
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                        color: t.tokens.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                      })}
                    >
                      {space.description}
                    </Typography>
                  ) : null}
                  <Box
                    sx={(t) => ({
                      mt: 1.5,
                      pt: 1.25,
                      borderTop: `1px solid ${t.tokens.border.subtle}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    })}
                  >
                    <Typography sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary })}>
                      {formatDate(space.created_at)}
                    </Typography>
                    <Box sx={(t) => ({
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.875,
                      py: 0.25,
                      borderRadius: `${t.ds.radius.full}px`,
                      bgcolor: t.tokens.surface.sunken,
                      color: t.tokens.text.secondary,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.005em',
                    })}>
                      {space.query_count || 0} {space.query_count === 1 ? 'insight' : 'insights'}
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
      
      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="sm"
        title="Create New Category"
        busy={creating}
        primaryAction={{ label: 'Create', onClick: handleCreate }}
        secondaryAction={{ label: 'Cancel', onClick: () => setCreateOpen(false), color: 'inherit' }}
      >
        {createErr && <Alert severity="error" sx={{ mb: 2 }}>{createErr}</Alert>}
        <MuiTextField
          autoFocus
          label="Category Name"
          fullWidth
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          sx={{ mt: 0.5, mb: 2 }}
          disabled={creating}
        />
        <MuiTextField
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={createDesc}
          onChange={(e) => setCreateDesc(e.target.value)}
          disabled={creating}
        />
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size="sm"
        title="Edit Category"
        busy={saving}
        primaryAction={{ label: 'Save', onClick: handleEdit }}
        secondaryAction={{ label: 'Cancel', onClick: () => setEditOpen(false), color: 'inherit' }}
      >
        {editErr && <Alert severity="error" sx={{ mb: 2 }}>{editErr}</Alert>}
        <MuiTextField
          autoFocus
          label="Category Name"
          fullWidth
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          sx={{ mt: 0.5, mb: 2 }}
          disabled={saving}
        />
        <MuiTextField
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={editDesc}
          onChange={(e) => setEditDesc(e.target.value)}
          disabled={saving}
        />
      </AppDialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        tone="destructive"
        title="Delete category?"
        confirmLabel="Delete"
        busy={deleting}
        description={
          <>
            Are you sure you want to delete the category <strong>{delSpace?.name}</strong>?
            This will also delete all {delSpace?.query_count || 0}{' '}
            {delSpace?.query_count === 1 ? 'insight' : 'insights'} in it. This action cannot be undone.
          </>
        }
      />
    </Box>
  );
}
